import React from 'react';
import type { GamPacingStatus } from '../../services/currentCampaignsApi';

export type StatusVariant =
  | 'live'
  | 'ok'
  | 'pending'
  | 'approved'
  | 'draft'
  | 'flagged'
  | 'featured'
  | 'partner'
  | 'paused';

interface StatusPillProps {
  variant: StatusVariant;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  size?: 'sm' | 'md';
  uppercase?: boolean;
  /** Hide the leading status dot. Use for inline flags / call-outs in tables. */
  noDot?: boolean;
  /**
   * 'quiet' is for the expected, everyday state in a long list (an approved
   * plan among mostly approved plans): no fill and muted sentence-case text,
   * so the filled exceptions around it stand out. Positive variants lead with
   * a ✓ instead of the dot.
   */
  appearance?: 'filled' | 'quiet';
}

/** Variants whose quiet form reads as done/fine, so they get a ✓. */
const QUIET_CHECK_VARIANTS: ReadonlySet<StatusVariant> = new Set<StatusVariant>(['ok', 'live', 'approved']);

const palette: Record<StatusVariant, { bg: string; fg: string; dot: string; pulse: boolean }> = {
  live:     { bg: 'var(--success-soft)', fg: 'var(--success)', dot: 'var(--success)', pulse: true  },
  ok:       { bg: 'var(--success-soft)', fg: 'var(--success)', dot: 'var(--success)', pulse: false },
  pending:  { bg: 'var(--warning-soft)', fg: 'var(--warning)', dot: 'var(--warning)', pulse: false },
  approved: { bg: 'var(--info-soft)',    fg: 'var(--info)',    dot: 'var(--info)',    pulse: false },
  draft:    { bg: 'var(--cg-neutral-100)', fg: 'var(--fg-3)',  dot: 'var(--fg-4)',    pulse: false },
  flagged:  { bg: 'var(--danger-soft)',  fg: 'var(--danger)',  dot: 'var(--danger)',  pulse: false },
  featured: { bg: 'var(--accent-2-soft)', fg: 'var(--accent-2-soft-fg)', dot: 'var(--accent-2)', pulse: false },
  partner:  { bg: 'var(--partner-soft)', fg: 'var(--partner)', dot: 'var(--partner)', pulse: false },
  paused:   { bg: 'var(--cg-neutral-100)', fg: 'var(--fg-3)',  dot: 'var(--fg-4)',    pulse: false },
};

/**
 * The one pacing-status → pill mapping, shared by every surface that shows
 * order pacing (Current Campaigns tab, Campaign Reports tab) so the same
 * order never wears two different pills.
 */
export const PACING_PILL: Record<GamPacingStatus, { variant: StatusVariant; label: string }> = {
  ON_TRACK: { variant: 'ok', label: 'On track' },
  AHEAD: { variant: 'pending', label: 'Ahead' },
  BEHIND: { variant: 'flagged', label: 'Behind' },
  NOT_STARTED: { variant: 'draft', label: 'Not started' },
  COMPLETED: { variant: 'approved', label: 'Completed' },
};

const StatusPill: React.FC<StatusPillProps> = ({
  variant,
  children,
  style,
  size = 'sm',
  uppercase,
  noDot = false,
  appearance = 'filled',
}) => {
  const p = palette[variant];
  const quiet = appearance === 'quiet';
  // Filled pills default to uppercase; quiet ones to sentence case.
  const upper = uppercase ?? !quiet;
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: noDot ? 0 : quiet ? 5 : 6,
    padding: quiet ? 0 : size === 'sm' ? '3px 9px' : '4px 12px',
    borderRadius: 'var(--radius-pill)',
    fontSize: quiet ? (size === 'sm' ? 12 : 13) : size === 'sm' ? 10.5 : 11.5,
    fontWeight: quiet ? 600 : 700,
    letterSpacing: upper ? '0.06em' : 0,
    textTransform: upper ? 'uppercase' : 'none',
    background: quiet ? 'transparent' : p.bg,
    color: quiet ? 'var(--fg-3)' : p.fg,
    fontFamily: 'var(--font-sans)',
    whiteSpace: 'nowrap',
    ...style,
  };
  if (quiet && !noDot && QUIET_CHECK_VARIANTS.has(variant)) {
    return (
      <span style={base}>
        <span aria-hidden="true" style={{ color: p.dot, fontWeight: 800 }}>✓</span>
        {children ?? variant}
      </span>
    );
  }
  return (
    <span style={base}>
      {!noDot && (
        <span
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: p.dot,
            animation: p.pulse ? 'cgPulse 1.6s ease-in-out infinite' : undefined,
            flexShrink: 0,
          }}
        />
      )}
      {children ?? variant}
    </span>
  );
};

export default StatusPill;
